import React, { useContext, createContext, useEffect } from 'react'
import {
    ChatFieldsFragment,
    ClientPhoneFieldsFragment,
} from '~graphql/generated/graphql'
import { useStateWithDebounce } from '~utils/hooks/use-state-with-debounce'
import { useStateWithReset } from '~utils/hooks/use-state-with-reset'
import { useChatsContext } from '../chats-provider'
import { LocalStorageKeys, SetStateFn } from '~config/constants'
import { useLocalStorage } from '~utils/hooks/use-local-storage'

type ChatProviderType = {
    chat: ChatFieldsFragment
    defaultPhone: ClientPhoneFieldsFragment | undefined
    patientFilter: string[]
    setPatientFilter: (patientId: string[]) => void
    debouncedPatientFilter: string[]
    showSystem: boolean
    setShowSystem: (showSystem: boolean) => void
    debouncedShowSystem: boolean
    showErrors: boolean
    setShowErrors: SetStateFn<boolean>
    availablePhones: ClientPhoneFieldsFragment[]
    clientPhone: ClientPhoneFieldsFragment | undefined
    setClientPhone: SetStateFn<ClientPhoneFieldsFragment | undefined>
    resetClientPhone: () => void
    baseClientPhone: string | null | undefined
    refetchChatsList: () => void
}

export const ChatContext = createContext<ChatProviderType>(
    {} as ChatProviderType,
)

export const ChatProvider = ({
    chat,
    initialPatientFilter,
    children,
}: {
    chat: ChatFieldsFragment
    /**
     * Optional initial patient filter applied on first mount. Used by features
     * like OpenChatButton to pre-select the patient context.
     */
    initialPatientFilter?: string[]
    children: React.ReactNode
}) => (
    <ChatContext.Provider
        value={{ ...useChatValues(chat, initialPatientFilter) }}
    >
        {children}
    </ChatContext.Provider>
)

function useChatValues(
    chat: ChatFieldsFragment,
    initialPatientFilter?: string[],
): ChatProviderType {
    const { client } = chat
    const { refetchChatPreviews } = useChatsContext()
    const { phone, client_phones } = client

    const availablePhones = client_phones?.filter(p => p.is_active) ?? []

    const defaultPhone =
        availablePhones?.find(p => p.is_chckup_primary) ??
        availablePhones?.find(p => p.is_primary && p.sms_available) ??
        availablePhones?.find(p => p.is_primary) ??
        availablePhones?.find(p => p.sms_available) ??
        availablePhones?.find(p => p.phone === phone) ??
        availablePhones?.at(0)

    const [patientFilter, setPatientFilter, debouncedPatientFilter] =
        useStateWithDebounce<string[]>(initialPatientFilter ?? [])
    const [showSystem, setShowSystem, debouncedShowSystem] =
        useStateWithDebounce<boolean>(true)
    const [showErrors, setShowErrors] = useLocalStorage<boolean>(
        LocalStorageKeys.chatsShowErrorMsgs,
        true,
    )
    const [clientPhone, setClientPhone, resetClientPhone] = useStateWithReset<
        ClientPhoneFieldsFragment | undefined
    >(defaultPhone)

    useEffect(() => {
        setClientPhone(defaultPhone)
    }, [chat])

    return {
        chat,
        defaultPhone,
        patientFilter,
        setPatientFilter,
        debouncedPatientFilter,
        showSystem,
        setShowSystem,
        debouncedShowSystem,
        showErrors,
        setShowErrors,
        availablePhones,
        clientPhone,
        setClientPhone,
        resetClientPhone,
        baseClientPhone: phone,
        refetchChatsList: refetchChatPreviews,
    }
}

export const useChat = () => {
    return useContext(ChatContext)
}
